import { LowerCasePipe } from '../../filters/lower-case.pipe';
import { CurrencyPipe } from '../../filters/currency.pipe';
import { TranslatePipe } from '../../filters/translate.pipe';
import { ItemInfoPopoverComponent } from '../../components/item-info-popover/item-info-popover.component';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { ItemErrorComponent } from '../../components/item-form-error/item-form-error.component';
import { ItemNotificationComponent } from '../../components/item-notification/item-notification.component';
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import Config from '../../utils/config.service';
import CategoryService from '../../services/category.service';
import Session from '../../utils/session.service';
import UserService from '../../services/user.service';
import BusinessRestServices from '../../services/business-rest.service';
import AngularFilterHelperService from '../../services/angular-filter-helper.service';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import RoutingService from '../../services/routing.service';
import { MatSelectModule } from '@angular/material/select';

@Component({
	standalone: true,
	selector: 'booking-instant',
	templateUrl: './booking-instant.component.html',
	imports: [
		CommonModule, MatSelectModule, LowerCasePipe, CurrencyPipe, 
		TranslatePipe, ItemInfoPopoverComponent, LoaderOverlayComponent, 
		ItemErrorComponent, ItemNotificationComponent, ReactiveFormsModule
	]
})
export default class BookingInstantView implements OnInit {
	private config: Config = inject(Config);
	private categoryService: CategoryService = inject(CategoryService);
	private session: Session = inject(Session);
	private userService: UserService = inject(UserService);
	private businessRestServices: BusinessRestServices = inject(BusinessRestServices);
	private angularFilterHelperService: AngularFilterHelperService = inject(AngularFilterHelperService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private routingService: RoutingService = inject(RoutingService);
	
	vm: any = undefined;
	mainForm: FormGroup;
	otherPersonForm: FormGroup;
	
	constructor(private fb: FormBuilder) {
		this.vm = this;
		this.vm.descMaxLength = 300;
		this.mainForm = this.fb.group({
			languages: [[], Validators.required],
			communicationTypes: [[], Validators.required],
			comment: ['', [Validators.required, Validators.maxLength(300)]],
		});
		this.otherPersonForm = this.fb.group({
			careTakenCheckbox: [false],
			name: [''],
			idcode: [''],
		});
		this.otherPersonForm.get('careTakenCheckbox')?.valueChanges.subscribe((checked) => {
			const nameControl = this.otherPersonForm.get('name');
			const idcodeControl = this.otherPersonForm.get('idcode');

			if (checked) {
				nameControl?.setValidators(Validators.required);
				idcodeControl?.setValidators(Validators.required);
			} else {
				nameControl?.clearValidators();
				idcodeControl?.clearValidators();
			}

			nameControl?.updateValueAndValidity();
			idcodeControl?.updateValueAndValidity();
		});
	}
	
	ngOnInit() {
		this.routingService.validateAccess('booking').then(() => {
			this.checkAvailability();
		});
	};
	
	checkAvailability() {
		var category: any = this.angularRoutingHelperService.getQueryParam('category');
		var origin: any = this.getOrigin();
		var allowedCategories: Array<any> = ['GENERAL_MEDICAL_PRACTICE', 'PHARMACIST'];
		
		if (allowedCategories.indexOf(category) !== -1) {
			var searchParams: any = {
				subcategory: category,
				origin: origin
			};
			
			this.businessRestServices.getInstantlyAvailable(searchParams, (res) => {
				if (!!res) {
					this.loadSettings();
				} else {
					this.redirectToHome();
				}
			}, (err) => {
				this.redirectToHome();
			});
		} else {
			this.redirectToHome();
		}
	}
	
	redirectToHome() {
		this.angularRoutingHelperService.setPath('/');
	}
	
	loadSettings() {
		var rawData: any = window.sessionStorage.getItem(this.config.getBookingConfig().Instant.STORAGE_KEY);
		var booking: any = rawData ? JSON.parse(rawData) : {};
		var origin: any = this.getOrigin();
		
		if (booking && booking.service) {
			this.onResolve();
		} else {
			var params: any = {
				origin: origin
			};
			this.businessRestServices.getOnDemandSettings(params, (settings) => {
				this.vm.settings = settings;
				this.onResolve();
			});
		}
	}
	
	onResolve() {
		this.vm.onDemandSettings = this.vm.settings;
		this.vm.subcategory = this.angularRoutingHelperService.getQueryParam('category');
		this.vm.isPharmacist = this.vm.subcategory === 'PHARMACIST';
		this.vm.interview = this.angularRoutingHelperService.getQueryParam('interview');
		this.vm.user = this.session.getSession().user;
		this.vm.origin = this.getOrigin();
		this.vm.storageKey = this.config.getBookingConfig().Instant.STORAGE_KEY;
		this.vm.disclaimer = this.angularFilterHelperService.translate('Booking.Instant.Disclaimer_'
			+ (this.vm.isPharmacist ? 'Pharmacist' : 'Doctor'));
		this.vm.data = {
			method: 'chat',
			careTakenInfo: {}
		};
		
		this.getCategory();
		this.getLanguages();
		this.getBooking();
		this.getCommunicationTypes();
		
		this.vm.getLanguagesText = this.getLanguagesText;
		this.vm.getCommunicationTypeText = this.getCommunicationTypeText;
		this.vm.handleFormSubmit = this.handleFormSubmit;
		this.vm.getBusinessWalletLabel = this.getBusinessWalletLabel;
		
		this.vm.isInitialized = true;
	}

	getOrigin() {
		var origin: any = this.angularRoutingHelperService.getQueryParam('origin');
		if (!origin) {
			var userSession: any = this.session.getSession();
			origin = (userSession && userSession.user && userSession.user.origin)
				? userSession.user.origin
				: 'minudoc.ee';
		}
		return origin;
	}
	
	getBooking() {
		var settings;
		var rawData: any = window.sessionStorage.getItem(this.vm.storageKey);
		this.vm.booking = rawData ? JSON.parse(rawData) : {};
		
		var generalPractitionerSpecialCheck: any = undefined;
		if (this.vm.isPharmacist) {
			settings = this.vm.onDemandSettings.filter((item) => {
				return item.subcategory === 'PHARMACIST' && item.furtherSubcategory === 'PHARMACIST';
			})[0] || {};
			this.vm.booking.price = typeof settings.price !== 'undefined'
				? settings.price
				: this.config.getBookingConfig().Instant.Pharmacist.DEFAULT_PRICE;
			this.vm.booking.currency = this.config.getBookingConfig().Instant.Pharmacist.DEFAULT_CURRENCY;
			this.vm.booking.duration = typeof settings.timePerPrice !== 'undefined'
				? settings.timePerPrice
				: this.config.getBookingConfig().Instant.Pharmacist.DEFAULT_DURATION_MINUTES;
		} else if (this.vm.isMentalCheck) {
			settings = this.vm.onDemandSettings.filter((item) => {
				return item.subcategory === 'GENERAL_MEDICAL_PRACTICE' && item.furtherSubcategory === 'MENTAL_CHECK';
			})[0] || {};
			this.vm.booking.price = typeof settings.price !== 'undefined'
				? settings.price
				: this.config.getBookingConfig().Instant.Doctor.DEFAULT_PRICE;
			this.vm.booking.currency = this.config.getBookingConfig().Instant.Doctor.DEFAULT_CURRENCY;
			this.vm.booking.duration = typeof settings.timePerPrice !== 'undefined'
				? settings.timePerPrice
				: this.config.getBookingConfig().Instant.Doctor.DEFAULT_DURATION_MINUTES;
		} else {
			settings = this.vm.onDemandSettings.filter((item) => {
				return item.subcategory === 'GENERAL_MEDICAL_PRACTICE' && item.furtherSubcategory === 'FAMILY_PHYSICIAN';
			})[0] || {};
			this.vm.booking.price = typeof settings.price !== 'undefined'
				? settings.price
				: this.config.getBookingConfig().Instant.Doctor.DEFAULT_PRICE;
			this.vm.booking.currency = this.config.getBookingConfig().Instant.Doctor.DEFAULT_CURRENCY;
			this.vm.booking.duration = typeof settings.timePerPrice !== 'undefined'
				? settings.timePerPrice
				: this.config.getBookingConfig().Instant.Doctor.DEFAULT_DURATION_MINUTES;
			generalPractitionerSpecialCheck = 'gpOnly';
		}
		
		this.userService.getWalletWithPrice({
			pricing: { price: this.vm.booking.price },
			furtherSubcategory: settings.furtherSubcategory,
			generalPractitionerSpecialCheck: generalPractitionerSpecialCheck
		}).then((usedWallet) => {
			if (usedWallet.alternative) {
				if (usedWallet.coPayWallet) {
					this.vm.walletName = usedWallet.coPayWallet.wallet.name;
					this.vm.insuranceCoPay = true;
				} else {
					this.vm.walletName = usedWallet.wallet.name;
				}
			}
			this.vm.isCoveredByBusinessWallet = this.vm.booking.price && (!usedWallet.price || usedWallet.coPayWallet);
		}).catch((e) => {
			console.error(e);
		});
		
		if (this.vm.booking.subcategory === this.vm.subcategory) {
			this.mainForm.get('comment').setValue(this.vm.booking.comment);
			this.mainForm.get('languages').setValue(this.vm.booking.languages);
			this.mainForm.get('communicationTypes').setValue(this.vm.booking.communicationType);
			if (this.vm.booking.careTakenInfo
				&& this.vm.booking.careTakenInfo.name
				&& this.vm.booking.careTakenInfo.personalCode) {
				this.otherPersonForm.get('careTakenCheckbox').setValue(true);
				this.otherPersonForm.get('name').setValue(this.vm.booking.careTakenInfo.name);
				this.otherPersonForm.get('idcode').setValue(this.vm.booking.careTakenInfo.personalCode);
			}
		} else {
			window.sessionStorage.removeItem(this.vm.storageKey);
		}
	}
	
	getBusinessWalletLabel() {
		if (this.vm.insuranceCoPay) {
			return 'Services.Other.Insurance_Cover';
		} else if (this.vm.walletName?.toLowerCase() === 'iizi') {
			return 'Services.Other.Package_By';
		} else {
			return 'Services.Other.Covered_By';
		}
	}
	
	getCategory() {
		this.categoryService.getAll().then((categories) => {
			this.vm.category = categories.filter((category) => {
				return category.subcategory === this.vm.subcategory
					&& (!this.vm.furtherSubcategory || category.furtherSubcategory === this.vm.furtherSubcategory);
			})[0];
		});
	}
	
	getLanguages() {
		if (this.vm.origin == 'minudoc.ee') {
			this.vm.languageOptions = ['EST', 'RUS', 'ENG'];
		} else if (this.vm.origin == 'siffi.com') {
			this.vm.languageOptions = ['FIN', 'ENG', 'SWE', 'EST', 'RUS'];
		} else if (this.vm.origin == 'minudoc.fi') {
			this.vm.languageOptions = ['FIN', 'ENG', 'SWE'];
		} else if (this.vm.origin == 'minudoc.lv') {
			this.vm.languageOptions = ['LVA', 'RUS', 'ENG'];
		} else if (this.vm.origin == 'minudoc.lt') {
			this.vm.languageOptions = ['LTU', 'RUS', 'ENG'];
		} else if (this.vm.origin == 'minudoc.pl') {
			this.vm.languageOptions = ['POL', 'RUS', 'ENG'];
		}
	}
	
	getCommunicationTypes() {
		if (this.vm.subcategory && this.vm.subcategory === 'PHARMACIST') {
			this.vm.communicationTypeOptions = ['VIDEO', 'CHAT'];
		} else {
			this.vm.communicationTypeOptions = ['VIDEO', 'CHAT', 'PHONE'];
		}
	}
	
	getLanguagesText() {
		if (!this.mainForm.get('languages').value || !this.mainForm.get('languages').value.length) {
			return this.angularFilterHelperService.translate('Booking.Instant.Languages');
		}
		return Array.from(this.mainForm.get('languages').value, (lang) => {
			return this.angularFilterHelperService.translate('Services.Language.' + lang); 
		}).join(', ');
	}
	
	getCommunicationTypeText() {
		if (!this.mainForm.get('communicationTypes').value || !this.mainForm.get('communicationTypes').value.length) {
			return this.angularFilterHelperService.translate('Booking.CommunicationType.CommunicationTypes');
		}
		return this.angularFilterHelperService.translate('Booking.CommunicationType.' + this.mainForm.get('communicationTypes').value);
	}
	
	handleFormSubmit() {
		this.vm.submitted = true;
		if (!this.mainForm.valid) {
			this.mainForm.markAllAsTouched();
			return;
		}
		if (!this.otherPersonForm.valid) {
			this.otherPersonForm.markAllAsTouched();
			return;
		}
		
		if (this.vm.isProcessing) return;
		this.vm.isProcessing = true;
		
		var data: any = {
			origin: this.vm.origin,
			comment: this.mainForm.get('comment').value,
			languages: this.mainForm.get('languages').value,
			subcategory: this.vm.subcategory,
			communicationType: this.mainForm.get('communicationTypes').value
		};
		if (this.otherPersonForm.get('careTakenCheckbox').value
			&& this.otherPersonForm.get('name').value
			&& this.otherPersonForm.get('idcode').value) {
			data.careTakenInfo = {};
			data.careTakenInfo.name = this.otherPersonForm.get('name').value;
			data.careTakenInfo.personalCode = this.otherPersonForm.get('idcode').value;
		}
		if (this.vm.booking.id) data.id = this.vm.booking.id;
		if (this.vm.interview) {
			data.interview = {
				localId: this.vm.interview,
			};
		}
		
		window.sessionStorage.setItem(this.vm.storageKey, JSON.stringify(data));
		this.angularRoutingHelperService.clearQueryParamsDirty()
		this.angularRoutingHelperService.setPath('/booking/instant/checkout');
	}
	
}
